import React, { useEffect, useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { ActivityCategoryDTO, ActivityTypeDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useActivityTypes } from '../../hooks/useActivityTypeApi';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';

interface ActivityCategoryFormProps {
  initialValues: ActivityCategoryDTO;
  onSubmit: (data: ActivityCategoryDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  },
  settings: {
    id: 'settings',
    title: 'Settings'
  }
} as const;

const ActivityCategoryForm: React.FC<ActivityCategoryFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ActivityCategoryDTO>(initialValues);

  const { data: activityTypes = [] } = useActivityTypes();

  // Add validation rules
  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
    activityTypeId: [{ required: true, message: 'Activity Type is required' }],
  };

  // Initialize form validation
  const { errors, validate } = useFormValidation<ActivityCategoryDTO>(validationRules);

  const fetchActivityTypes = async () => {
    try {
      const response = await activityTypeApi.search({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setActivityTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching activity types:', error);
    }
  };

  useEffect(() => {
    fetchActivityTypes();
  }, []);

  // Update handleSave to include validation
  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection 
        id={FORM_SECTIONS.basicInfo.id} 
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="activityTypeId"
          label="Activity Type"
          type="dropdown"
          value={formData.activityTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Exercise Template"
          required={true}
          error={errors.activityTypeId}
          options={activityTypes.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
        />
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
      </FormSection>

      <FormSection 
        id={FORM_SECTIONS.settings.id} 
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Display Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          required={true}
          error={errors.displayPosition}
          helper="Lower numbers will be displayed first"
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active"
        />
      </FormSection>
    </Form>
  );
};

export default ActivityCategoryForm;
